
<template  >
  <div id="gallery">
    <div
      class="layer-bg layer parallax"
      id="bg"
      data-depth="0.00"
      :style="!hasMultipleImages ? { backgroundImage: `url(${content.background_image})` } : {}"
    >
      <div v-if="hasMultipleImages" class="bg-top-layer" />
      <div v-if="hasMultipleImages" class="bg-middle-layer" />
      <div v-if="hasMultipleImages" class="bg-bottom-layer" />
    </div>
    <div class="layer-middle layer parallax flex" data-depth="0.40">
      <div
        class="w-full"
        :class="hasMultipleImages ? 'middle-image-container' : ''"
        v-for="(image, index) in content.middle_images"
        :key="image + index"
      >
        <div
          class="absolute multiple-images-button-container"
          :class="hasMultipleImages ? 'multiple-images-button-container-bottom' : ''"
        >
          <img
            :class="hasMultipleImages ? 'middle-image-multiple' : ''"
            class="middle-image"
            :id="`middleImage${index}`"
            :src="image"
            :style="hasMultipleImages ? { height: '60vh' } : {}"
            @load="imageLoaded(index)"
          />
          <div
            v-if="hasMultipleImages"
            class="static-button static-button-multiple"
            @click="handleVideoChange(true, content.videos[index])"
            :style="{
              bottom: `${content.button_coordinates[index].bottom}%`,
              left: `${content.button_coordinates[index].left}%`,
            }"
          >
            <play-icon />
          </div>
        </div>
        <p
          v-if="hasMultipleImages"
          class="middle-image-text"
          :style="{ bottom: `calc(25vh - 100px + ${content.additional_margin_texts[index]}px)` }"
          v-html="$t(`texts[${index}]`)"
        ></p>
      </div>

      <div v-if="!hasMultipleImages" class="button-layer">
        <div
          v-for="(button, index) in content.buttons"
          :key="`button${index}`"
          :style="{ left: `${middleImageWidth * (button.x / 100)}px`, top: `${button.y}vh` }"
          class="static-button"
          @click="handleVideoChange(true, button.video)"
        >
          <play-icon />
        </div>
      </div>
    </div>
    <div
      class="layer-fg layer parallax flex w-full"
      :class="hasMultipleImages ? 'fg-multiple-images' : ''"
      :data-depth="hasMultipleImages ? 1.3 : 0.8"
    >
      <img v-for="(image, index) in content.foreground_images" :key="image + index" :src="image" />
    </div>
  </div>
</template>

<script>
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import PlayIcon from "./PlayIcon";
export default {
  name: "SectionGallery",
  data() {
    return {
      middleImageWidth: 0,
      shouldPlayVideo: false,
      currentVideo: "",
      leftMarginVideo: 0,
      hasMultipleImages: false,
    };
  },
  components: {
    PlayIcon,
  },
  props: {
    content: {
      type: Object,
      required: true,
    },
    nextSection: {
      type: String,
      required: false,
    },
  },

  methods: {
    imageLoaded(index) {
      if (index === 0) {
        this.$store.commit("setImageReady");
        //getting the main image width, in order to position the button layers relative to it
        let middleImage = document.getElementById("middleImage0");
        this.middleImageWidth = middleImage.offsetWidth;

        let imageContainers = document.getElementsByClassName("middle-image-container");

        imageContainers.forEach((container) => {
          container.style.width = `${this.middleImageWidth}px`;
        });

        let imageButtonContainers = document.getElementsByClassName("multiple-images-button-container");
        imageButtonContainers.forEach((container) => {
          container.style.width = `${this.middleImageWidth}px`;
          container.style.height = `${middleImage.offsetHeight}px`;
        });

        //after the main image is loaded and we know its size, we set up the gallery width and the animation
        this.setContainerWidth();
      }
    },

    handleVideoChange(value, video) {
      this.shouldPlayVideo = value;
      this.$store.commit("handleVideoChange", { value, video });
      this.currentVideo = video;

      if (value) {
        //audio should start when video modal opens
        let audio = document.getElementById("audio-player");
        audio.play();
        this.$store.commit("handleAudioChange", true);

        //positioning the video modal in center of the screen - position fixed conflicts with gsap
        let hero = document.getElementById("gallery");
        this.leftMarginVideo = -hero.getBoundingClientRect().x;
      }
    },

    setContainerWidth() {
      //we establish the width of the gallery section, taking into consideration if we have one or multiple main images
      let bg = document.getElementById("bg");
      let gallery = document.getElementById("gallery");
      let gallerySection = document.querySelector(".section-large");

      let containerWidth = 0;
      gsap.utils.toArray(".middle-image").forEach((layer) => {
        containerWidth += layer.offsetWidth;
      });

      const finalWidth = `calc(${containerWidth}px + ${
        this.hasMultipleImages ? this.content.middle_images.length * 80 : 80
      }vw)`;

      bg.style.width = finalWidth;
      gallery.style.width = finalWidth;
      gallerySection.style.width = finalWidth;

      //after setting up the gallery section width, we use it in order to set up the animation correctly
      let hero = document.getElementById("gallery");
      const sections = gsap.utils.toArray(".before-gallery");

      //horizontal distance until the gallery section
      let beforeWidth = 0;
      sections.forEach((section) => {
        beforeWidth += section.offsetWidth;
      });

      let viewportWidth = document.documentElement.clientWidth;

      const tl = gsap.timeline({
        scrollTrigger: {
          x: 0,
          trigger: hero,
          start: `+=${beforeWidth + viewportWidth / 2}`, //horizontal distance in pixels
          end: beforeWidth + hero.offsetWidth,
          scrub: 1,
        },
      });

      //setting up different moving speeds for each section
      gsap.utils.toArray(".parallax").forEach((layer) => {
        const depth = layer.dataset.depth;
        const movement = -(layer.offsetWidth * depth * 2);
        tl.to(layer, { x: movement, ease: "none" }, 0);
      });
    },
  },

  mounted() {
    this.hasMultipleImages = this.content.middle_images.length > 1;
    gsap.registerPlugin(ScrollTrigger);
  },

  destroyed() {
    this.$store.commit("resetImageReady");
  },
};
</script>

<style lang="postcss" scoped>
#gallery {
  height: 100vh;
  overflow: hidden;
  position: relative;
  width: 100%;
  pointer-events: none;
}

.layer {
  background-position: bottom center;
  background-size: cover;
  background-repeat: no-repeat;
  height: 100%;
  position: fixed;
  z-index: -1;
}

#gallery,
.layer {
  min-height: 100vh;
}

.layer-bg {
  width: 100%;
}

.layer-middle img {
  height: 100vh;
  bottom: 0px;
  max-width: unset !important;
  margin-left: 100vw;
}

.layer-middle {
  width: -webkit-fill-available;
  background-size: contain;
}
.layer-fg {
  background-repeat: space;
  background-size: contain;
  width: 400vw;
}

.layer-fg img {
  margin-left: 100vw;
  pointer-events: none;
  max-width: 130vw;
  max-width: 700px;
}

.fg-multiple-images img:nth-child(4) {
  max-width: 1000px;
}

.fg-multiple-images img {
  margin-left: 230vw;
}
/* .fg-multiple-images img:nth-child(1) {
  margin-left: 150vw;
}

.fg-multiple-images img:nth-child(2) {
  margin-left: 250vw;
}

.fg-multiple-images img:nth-child(3) {
  margin-left: 290vw;
}

.fg-multiple-images img:nth-child(4) {
  margin-left: 300vw;
} */

@media only screen and (max-width: 1100px) {
  .layer-fg img {
    margin-left: 200vw;
  }
}

@media only screen and (max-width: 768px) {
  .layer-fg img {
    margin-left: 300vw;
  }
}

@media only screen and (max-width: 500px) {
  .layer-fg img {
    margin-left: 300vw;
  }
}

.button-layer {
  width: 100%;
  height: 100%;
  margin-left: 100vw;
  height: 100vh;
  bottom: 0px;
  position: absolute;
  z-index: 1000;
}

.static-button {
  background: rgba(173, 32, 59, 0.6);
  border: 2px solid #ffffff;
  border-radius: 50px;
  width: 50px;
  height: 50px;
  position: absolute;
  z-index: 2000;
  padding: 10px;
  transition: all 0.7s;
  cursor: pointer;
  pointer-events: all;
}

.static-button:hover {
  background: rgb(173, 32, 58);
}

.middle-image-text {
  bottom: calc(25vh - 100px);
  position: absolute;
  color: #ffffff;
  font-size: 13px;
  letter-spacing: 0.5px;
  line-height: 20px;
  height: 120px;
}

.middle-image-container {
  margin-left: 80vw;
  position: relative;
  min-width: 300px;
}

.middle-image-multiple {
  position: absolute !important;
  left: 0 !important;
  right: 0 !important;
  margin-left: 0px !important;
  margin-right: auto !important;
  margin-top: 0px !important;
}

.multiple-images-button-container-bottom {
  bottom: 24vh !important;
}

.bg-top-layer {
  background: #a0bfd8;
  width: 100%;
  height: 60vh;
}
.bg-middle-layer {
  background: #6f8b5e;
  width: 100%;
  height: 3vh;
}
.bg-bottom-layer {
  background: #415a35;
  width: 100%;
  height: 37vh;
}
</style>
